import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useNLPSearch from 'app/components/NLPSearch/hooks/useNLPSearch';
import { AnimatePresence } from 'framer-motion';
import useOnClickOutside from 'hooks/click-outside';
import {
  isNLPLoadingSelector,
  promptSelector,
  setFeedback,
  setFound,
  setIsNLPLoading,
  setNotFound,
  setPrompt,
  setPromptUuid,
  setShowNLPError,
  setShowNoResults,
  showNoResultsSelector,
} from 'store/reducers/nlp/nlp-reducer';
import {
  setCurrentTabs,
  showingLoadingScreenSelector,
} from 'store/reducers/resources-steps-reducer/resources-steps-reducer';
import {
  emptyAllFilters,
  isResourcesLoadingSelector,
  previousFiltersLengthSelector,
  previousFiltersSelector,
  setCurrentStep,
  setFilters,
  setIsResourcesLoading,
  setResources,
  setSelectedCriteria,
  setUuids,
  updatePreviousFilters,
} from 'store/reducers/resources/resources-reducer';
import { ThemeProvider } from 'styled-components';

import { ResourcesContext } from '../../ResourcesContext';
import ResourcesNLPSearchBar from './ResourcesNLPSearchBar';
import ResourcesNLPSearchBarHistory from './ResourcesNLPSearchBar/ResourcesNLPSearchBarHistory';
import { ResourcesNLPSearchContext, ResourcesNLPSearchInfo } from './ResourcesNLPSearchContext';
import { ResourcesNLPSearchWrapper } from './styled';
import useResourcesSearchHistory from './useResourcesSearchHistory';

export default function ResourcesNLPSearch() {
  const dispatch = useDispatch();

  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const previousFilters = useSelector(previousFiltersSelector);
  const prompt = useSelector(promptSelector);
  const isNlpLoading = useSelector(isNLPLoadingSelector);
  const filters = useSelector(previousFiltersLengthSelector);
  const isResourcesLoading = useSelector(isResourcesLoadingSelector);
  const showLoadingScreen = useSelector(showingLoadingScreenSelector);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>(() => {
    if (prompt) {
      return prompt;
    }

    return '';
  });

  const { setPerfectMatchedFilters } = useContext(ResourcesContext);

  const isLoading = useMemo<boolean>(() => {
    return isNlpLoading || (isResourcesLoading && filters > 0);
  }, [isNlpLoading, isResourcesLoading, filters]);

  const [currentFilter, setCurrentFilter] = useState(null);
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');
  const showNoResults = useSelector(showNoResultsSelector);
  const { saveSearch, getSearches } = useResourcesSearchHistory();
  const nlpSearchQuery = useNLPSearch(
    { query, request_origin: 'resource_library' },
    { enabled: query !== '', cacheTime: 0 }
  );

  const sendSearch = (search: string = null) => {
    const prompt = search || searchValue;

    if (prompt && prompt !== query) {
      saveSearch(prompt);
      setPerfectMatchedFilters([]);
      setQuery(prompt);

      if (showHistory) {
        setShowHistory(false);
      }
    }
  };

  useEffect(() => {
    if (prompt === null) {
      setQuery('');
      setSearchValue('');
    } else {
      setSearchValue(prompt);
      setQuery(prompt);
    }
  }, [prompt]);

  useEffect(() => {
    return () => {
      setQuery('');
      setSearchValue('');
    };
  }, []);

  useOnClickOutside(searchWrapperRef, (e) => {
    const history = document.querySelector('[data-id="resources-nlp-search-bar-history"]');

    if (history && !history.contains(e.target as Node)) {
      setShowHistory(false);
    }
  });

  useEffect(() => {
    const searches = getSearches();
    if (searchFocused && searches.length > 0 && !isLoading && !showLoadingScreen) {
      setShowHistory(true);
    }
  }, [searchFocused, isLoading, showLoadingScreen]);

  const initialValues = useMemo<ResourcesNLPSearchInfo>(
    () => ({
      searchValue,
      currentFilter,
      searchFocused,
      nlpSearchQuery,
      searchInputRef,
      sendSearch,
      setSearchValue,
      setCurrentFilter,
      setSearchFocused,
      showHistory,
      setShowHistory,
    }),
    [
      searchValue,
      currentFilter,
      searchFocused,
      searchInputRef,
      nlpSearchQuery.data,
      nlpSearchQuery.isLoading,
      showHistory,
    ]
  );

  useEffect(() => {
    dispatch(setIsNLPLoading(nlpSearchQuery.isLoading));
  }, [nlpSearchQuery.isLoading]);

  const arePreviousAndCurrentFiltersEqual = (filters) => {
    const previousFiltersKeys = Object.keys(previousFilters).filter(
      (key) => previousFilters[key].length > 0 || previousFilters[key] === true
    );
    const filtersKeys = Object.keys(filters);

    if (previousFiltersKeys.length !== filtersKeys.length) {
      return false;
    }

    for (const key of previousFiltersKeys) {
      if (!filtersKeys.includes(key)) {
        return false;
      }

      const previousFiltersValues = previousFilters[key];
      const filtersValues = filters[key];

      if (previousFiltersValues.length !== filtersValues.length) {
        return false;
      }

      for (const value of previousFiltersValues) {
        if (filtersValues.find((filter) => filter.uuid === value.uuid) === undefined) {
          return false;
        }
      }
    }

    return true;
  };

  useEffect(() => {
    const { data, isLoading, isError } = nlpSearchQuery;

    if (data && !isLoading && !isError) {
      dispatch(setCurrentTabs(['most_relevant']));
      dispatch(setSelectedCriteria(null));
      dispatch(setPrompt(query));
      dispatch(setPromptUuid(data.search_term_prompt));
      dispatch(setFeedback(null));

      if (Object.keys(data.found).length > 0) {
        const exclusiveFilters = {};

        const filtersKeys = Object.keys(data.found).filter((key) => key !== 'people');
        const filters: any = filtersKeys.reduce((acc, key) => {
          acc[key] = data.found[key];
          return acc;
        }, {});

        const finalFilters = {
          filters: {
            ...filters,
            formats: filters.resource_formats || [],
          },
          exclusive_filters: exclusiveFilters,
        };

        if (showNoResults) {
          dispatch(setShowNoResults(false));
        }

        dispatch(setFilters(finalFilters));

        dispatch(setFound(data.found));
        dispatch(updatePreviousFilters({}));

        if (arePreviousAndCurrentFiltersEqual(data.found)) {
          dispatch(setIsResourcesLoading(false));
        }
        dispatch(setCurrentStep(2));
      } else {
        dispatch(setShowNoResults(true));
        dispatch(setFound(null));
        dispatch(emptyAllFilters({}));
        dispatch(setResources([]));
        dispatch(setUuids([]));
        dispatch(setCurrentStep(4));
      }

      if (Object.keys(data.not_found).length > 0) {
        dispatch(setNotFound(data.not_found));
        dispatch(updatePreviousFilters({}));
      } else {
        dispatch(setNotFound(null));
        dispatch(updatePreviousFilters({}));
      }

      dispatch(setIsNLPLoading(false));
    }
  }, [nlpSearchQuery.data, nlpSearchQuery.isLoading]);

  useEffect(() => {
    if (nlpSearchQuery.isError) {
      dispatch(setShowNLPError(true));
    }
  }, [nlpSearchQuery.isError]);

  return (
    <ThemeProvider theme={{ hasSearchValue: searchValue !== '', isFocused: searchFocused }}>
      <ResourcesNLPSearchContext.Provider value={initialValues}>
        <ResourcesNLPSearchWrapper ref={searchWrapperRef}>
          <ResourcesNLPSearchBar />
          <div style={{ position: 'relative', width: '100%' }}>
            <AnimatePresence>
              {showHistory ? <ResourcesNLPSearchBarHistory /> : null}
            </AnimatePresence>
          </div>
        </ResourcesNLPSearchWrapper>
      </ResourcesNLPSearchContext.Provider>
    </ThemeProvider>
  );
}
